import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials, colones } from "@/lib/api";
import {
  ShieldCheck, Building2, Mail, Phone, FileText, Calendar, CheckCircle2, XCircle,
  Search, Users, Lock, Unlock, CreditCard, Receipt, Eye, AlertTriangle, Download, Trash2,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { computeSuscripcion } from "@/lib/subscription";

const TABS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "activo", label: "Aprobadas" },
  { key: "rechazado", label: "Rechazadas" },
  { key: "clientes", label: "Clientes" },
];

export default function AdminCuentasPendientes() {
  const [tab, setTab] = useState("pendiente");
  return (
    <Layout>
      <header className="mb-6 fade-in-up">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <ShieldCheck size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Panel de Control</div>
            <h1 className="font-display text-3xl font-semibold">Super Administrador</h1>
          </div>
        </div>
        <p className="text-sm text-secondary-c">Gestioná las cuentas nuevas, aprobás/rechazás solicitudes y administrás tus clientes SaaS: pagos recibidos, comprobantes y bloqueo por mora.</p>
      </header>

      <div className="np-card p-1 mb-5 flex items-center gap-1 flex-wrap w-fit">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-primary-c text-white" : "hover:bg-[#F0F0EA] text-primary-c"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "clientes" ? <ClientesTab /> : <CuentasTab estado={tab} />}
    </Layout>
  );
}

// ============================================================================
// TAB: Pendientes / Aprobadas / Rechazadas
// ============================================================================
function CuentasTab({ estado }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cuentas-pendientes?estado=${estado}`);
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las cuentas");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [estado]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(u => {
      const emp = u.empresa || {};
      return `${u.name} ${u.email} ${emp.razon_social || ""} ${emp.cedula_juridica || ""}`.toLowerCase().includes(s);
    });
  }, [items, q]);

  const aprobar = async (u) => {
    try {
      await api.put(`/admin/cuentas-pendientes/${u.user_id}/aprobar`);
      toast.success(`Cuenta aprobada: ${u.name}`);
      setConfirmar(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
  };
  const rechazar = async (u, motivo) => {
    try {
      await api.put(`/admin/cuentas-pendientes/${u.user_id}/rechazar`, { motivo });
      toast.success(`Cuenta rechazada: ${u.name}`);
      setConfirmar(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
  };

  return (
    <>
      <div className="np-card mb-5 p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
          <input className="np-input pl-9" placeholder="Buscar por empresa, nombre, cédula o correo…" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-cuentas-search" />
        </div>
      </div>

      {loading ? (
        <div className="np-card p-10 text-center text-sm text-secondary-c">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="np-card p-12 text-center" data-testid="admin-cuentas-empty">
          <Users size={40} className="mx-auto text-secondary-c opacity-40 mb-3" />
          <div className="font-medium">No hay cuentas {estado === "pendiente" ? "pendientes" : estado === "activo" ? "aprobadas" : "rechazadas"}</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-cuentas-list">
          {filtered.map(u => {
            const emp = u.empresa || {};
            return (
              <div key={u.user_id} className="np-card border-l-4 border-primary-c" data-testid={`admin-cuenta-row-${u.user_id}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-full bg-[#F0F0EA] text-primary-c flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {initials(u.name || u.email)}
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-lg">{emp.razon_social || <span className="italic text-secondary-c">(empresa por completar)</span>}</div>
                      {u.estado === "pendiente" && <span className="np-badge np-badge-warning text-[10px]">Pendiente</span>}
                      {u.estado === "activo" && <span className="np-badge np-badge-success text-[10px]">Aprobada</span>}
                      {u.estado === "rechazado" && <span className="np-badge np-badge-error text-[10px]">Rechazada</span>}
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Mail size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Correo:</span>
                        <a href={`mailto:${u.email}`} className="text-primary-color hover:underline font-medium">{u.email}</a>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Calendar size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Solicitado:</span>
                        <strong>{fmtFecha(u.created_at)}</strong>
                      </div>
                      {(emp.cedula_juridica || emp.telefono) && (
                        <>
                          <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                            <FileText size={13} className="text-secondary-c" />
                            <span className="text-secondary-c">Cédula jurídica:</span>
                            <strong>{emp.cedula_juridica || "—"}</strong>
                          </div>
                          <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                            <Phone size={13} className="text-secondary-c" />
                            <span className="text-secondary-c">Teléfono:</span>
                            <strong>{emp.telefono || "—"}</strong>
                          </div>
                        </>
                      )}
                      {u.rechazo_motivo && (
                        <div className="p-2 rounded-lg bg-[#FDEDED] border border-[#F0C6C6] text-error md:col-span-2">
                          <strong>Motivo del rechazo:</strong> {u.rechazo_motivo}
                        </div>
                      )}
                    </div>
                  </div>
                  {u.estado === "pendiente" && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setConfirmar({ action: "aprobar", user: u })}
                        className="np-btn np-btn-primary text-sm"
                        data-testid={`admin-approve-${u.user_id}`}
                      >
                        <CheckCircle2 size={14} /> Aprobar
                      </button>
                      <button
                        onClick={() => setConfirmar({ action: "rechazar", user: u })}
                        className="np-btn np-btn-outline text-sm border-[#F0C6C6] text-error hover:bg-[#FDEDED]"
                        data-testid={`admin-reject-${u.user_id}`}
                      >
                        <XCircle size={14} /> Rechazar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmar && (
        <ConfirmModal
          action={confirmar.action}
          user={confirmar.user}
          onCancel={() => setConfirmar(null)}
          onConfirm={(motivo) => confirmar.action === "aprobar" ? aprobar(confirmar.user) : rechazar(confirmar.user, motivo)}
        />
      )}
    </>
  );
}

function ConfirmModal({ action, user, onCancel, onConfirm }) {
  const [motivo, setMotivo] = useState("");
  const isRechazar = action === "rechazar";
  const emp = user.empresa || {};
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onCancel}>
      <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="admin-confirm-modal">
        <h3 className="font-display text-xl font-semibold mb-2">{isRechazar ? "Rechazar cuenta" : "Aprobar cuenta"}</h3>
        <p className="text-sm text-secondary-c mb-4">
          {isRechazar ? "Estás por rechazar" : "Estás por aprobar"} la cuenta {emp.razon_social ? <>de <strong className="text-primary-c">{emp.razon_social}</strong> </> : ""}(<strong>{user.email}</strong>).
        </p>
        {isRechazar && (
          <div className="mb-4">
            <label className="np-label">Motivo (opcional)</label>
            <textarea className="np-input min-h-[80px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: Datos incompletos, empresa no verificable, etc." data-testid="admin-reject-motivo" />
          </div>
        )}
        {!isRechazar && (
          <div className="p-3 rounded-xl bg-[#E6EFEA] border border-primary-c text-sm text-primary-c mb-4">
            Se activará la cuenta y se enviará un correo al usuario avisándole.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="np-btn np-btn-ghost" data-testid="admin-confirm-cancel">Cancelar</button>
          <button onClick={() => onConfirm(motivo)} className={`np-btn np-btn-primary ${isRechazar ? "!bg-error" : ""}`} data-testid="admin-confirm-ok">
            {isRechazar ? "Rechazar" : "Aprobar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: Clientes
// ============================================================================
function ClientesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detalle, setDetalle] = useState(null);          // empresa seleccionada
  const [confirmar, setConfirmar] = useState(null);      // {action, empresa}
  const [eliminar, setEliminar] = useState(null);        // empresa seleccionada para eliminar
  const [descargando, setDescargando] = useState(null);  // empresa_id en descarga

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clientes");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los clientes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(c => {
      const emp = c.empresa || {}, own = c.owner || {};
      return `${emp.razon_social || ""} ${emp.cedula_juridica || ""} ${own.email || ""}`.toLowerCase().includes(s);
    });
  }, [items, q]);

  // Total de pagos pendientes en todos los clientes (para banner de alerta)
  const totalPendientes = useMemo(() => items.reduce((s, c) => s + (c.pagos_pendientes || 0), 0), [items]);

  // Totales de mensualidades mensualizadas: Empresas Fee (es_gratuita=true, aunque no se cobran)
  // vs empresas con suscripción de pago activa.
  const totalesMensuales = useMemo(() => {
    let fee = 0, sub = 0, feeCount = 0, subCount = 0;
    for (const c of items) {
      const emp = c.empresa || {};
      const sus = computeSuscripcion(emp, c.empleados_activos || 0);
      if (!sus) continue;
      // Normaliza a mensualidad (base + porColab*nColabs, sin descuento por ciclo)
      const plan = sus.plan;
      const mensual = (plan.base + plan.porColab * (c.empleados_activos || 0));
      if (emp.es_gratuita) { fee += mensual; feeCount++; }
      else { sub += mensual; subCount++; }
    }
    return { fee, sub, feeCount, subCount };
  }, [items]);

  const bloquear = async (empresa_id, motivo) => {
    try {
      await api.put(`/admin/clientes/${empresa_id}/bloquear`, { motivo });
      toast.success("Cliente bloqueado. No podrá iniciar sesión.");
      setConfirmar(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo bloquear"); }
  };
  const desbloquear = async (empresa_id) => {
    try {
      await api.put(`/admin/clientes/${empresa_id}/desbloquear`);
      toast.success("Cliente reactivado.");
      setConfirmar(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo desbloquear"); }
  };

  const toggleGratuita = async (empresa_id, es_gratuita) => {
    try {
      await api.put(`/admin/clientes/${empresa_id}/toggle-gratuita`, { es_gratuita });
      toast.success(es_gratuita
        ? "Empresa marcada como gratuita. No se cobrará suscripción."
        : "Empresa vuelve al esquema de pago normal.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const descargarZip = async (empresa) => {
    setDescargando(empresa.id);
    try {
      const res = await api.get(`/admin/clientes/${empresa.id}/export`, { responseType: "blob" });
      // Extraer nombre del header si viene, sino armar uno
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m?.[1] || `verticepro_${(empresa.razon_social || "cliente").replace(/[^a-z0-9]/gi, "_")}_${empresa.id}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Descarga lista: ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar el respaldo");
    } finally { setDescargando(null); }
  };

  const eliminarCliente = async (empresa) => {
    try {
      const { data } = await api.delete(`/admin/clientes/${empresa.id}`);
      const total = Object.values(data?.deleted || {}).reduce((s, n) => s + Number(n || 0), 0);
      toast.success(`Cliente "${empresa.razon_social}" eliminado (${total} registros borrados).`);
      setEliminar(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar el cliente"); }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-testid="admin-totales-mensuales">
        <div className="np-card p-4 border-l-4 border-primary-color bg-[#E6EFEA]/40">
          <div className="text-[11px] uppercase tracking-widest text-secondary-c font-semibold mb-1">Mensualidades — Empresas con suscripción</div>
          <div className="text-2xl font-semibold text-primary-color flex items-baseline gap-1.5">
            <CreditCard size={20} /> {colones(totalesMensuales.sub)}
          </div>
          <div className="text-[11px] text-secondary-c mt-1">
            {totalesMensuales.subCount} cliente{totalesMensuales.subCount === 1 ? "" : "s"} · Suma mensualizada (sin descuentos por ciclo)
          </div>
        </div>
        <div className="np-card p-4 border-l-4 border-[#A67115] bg-[#FFF7E5]/60">
          <div className="text-[11px] uppercase tracking-widest text-secondary-c font-semibold mb-1">Mensualidades — Empresas Fee</div>
          <div className="text-2xl font-semibold text-[#A67115] flex items-baseline gap-1.5">
            <Package size={20} /> {colones(totalesMensuales.fee)}
          </div>
          <div className="text-[11px] text-secondary-c mt-1">
            {totalesMensuales.feeCount} cliente{totalesMensuales.feeCount === 1 ? "" : "s"} · Cortesía (no se cobra) · Referencia si pasaran a pago
          </div>
        </div>
      </div>
      {totalPendientes > 0 && (
        <div className="np-card mb-4 p-4 border-l-4 border-warning bg-[#FFF9EC] flex items-start gap-3" data-testid="alerta-pagos-pendientes">
          <div className="w-9 h-9 rounded-xl bg-warning/15 text-warning flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-primary-c">
              {totalPendientes} pago{totalPendientes > 1 ? "s" : ""} por revisar
            </div>
            <div className="text-xs text-secondary-c">
              Uno o más clientes registraron pagos que esperan tu aprobación. Al aprobar, el sistema actualiza automáticamente su próxima fecha de pago.
            </div>
          </div>
        </div>
      )}
      <div className="np-card mb-5 p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
          <input className="np-input pl-9" placeholder="Buscar cliente por empresa, cédula jurídica o correo…" value={q} onChange={e => setQ(e.target.value)} data-testid="clientes-search" />
        </div>
      </div>

      {loading ? (
        <div className="np-card p-10 text-center text-sm text-secondary-c">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="np-card p-12 text-center" data-testid="clientes-empty">
          <Building2 size={40} className="mx-auto text-secondary-c opacity-40 mb-3" />
          <div className="font-medium">Aún no tenés clientes activos.</div>
          <div className="text-xs text-secondary-c mt-1">Una empresa aparece acá después de aprobar su solicitud.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="clientes-list">
          {filtered.map(c => {
            const emp = c.empresa || {}, own = c.owner || {};
            const suspendida = emp.estado === "suspendido";
            const susc = computeSuscripcion(emp, c.empleados_activos || 0);
            return (
              <div key={emp.id} className={`np-card border-l-4 ${suspendida ? "border-error" : "border-primary-c"}`} data-testid={`cliente-row-${emp.id}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${suspendida ? "bg-[#FDEDED] text-error" : "bg-[#E6EFEA] text-primary-color"}`}>
                    <Building2 size={20} />
                  </div>
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-lg">{emp.razon_social || <span className="italic text-secondary-c">(sin razón social)</span>}</div>
                      {emp.es_gratuita
                        ? <span className="np-badge text-[10px] bg-[#E6EFEA] text-primary-c">✦ Gratuita</span>
                        : suspendida
                          ? <span className="np-badge np-badge-error text-[10px]"><Lock size={10} /> Suspendida</span>
                          : <span className="np-badge np-badge-success text-[10px]">Activa</span>}
                      {c.pagos_pendientes > 0 && !emp.es_gratuita && (
                        <span className="np-badge np-badge-warning text-[10px]">{c.pagos_pendientes} pago{c.pagos_pendientes > 1 ? "s" : ""} por revisar</span>
                      )}
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-secondary-c cursor-pointer ml-2 select-none" title="Empresas propias o de cortesía — no requieren pago">
                        <input
                          type="checkbox"
                          checked={!!emp.es_gratuita}
                          onChange={(e) => toggleGratuita(emp.id, e.target.checked)}
                          data-testid={`cliente-gratuita-${emp.id}`}
                          className="rounded border-secondary-c cursor-pointer"
                        />
                        Empresa gratuita (sin pago)
                      </label>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <FileText size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Céd. jurídica:</span>
                        <strong>{emp.cedula_juridica || "—"}</strong>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Mail size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Owner:</span>
                        <strong className="truncate">{own.email || "—"}</strong>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Phone size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Tel:</span>
                        <strong>{emp.telefono || "—"}</strong>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Calendar size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Último pago:</span>
                        <strong>{c.ultimo_pago ? `${fmtFecha(c.ultimo_pago.fecha_pago)} · ${colones(c.ultimo_pago.monto)}` : <span className="text-secondary-c italic">Sin pagos</span>}</strong>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FAFAF7] border border-c flex items-center gap-2">
                        <Receipt size={13} className="text-secondary-c" />
                        <span className="text-secondary-c">Total pagado:</span>
                        <strong>{colones(c.total_aprobado)}</strong>
                      </div>
                      {suspendida && emp.suspension_motivo && (
                        <div className="p-2 rounded-lg bg-[#FDEDED] border border-[#F0C6C6] text-error md:col-span-2 lg:col-span-3">
                          <AlertTriangle size={12} className="inline mr-1" />
                          <strong>Motivo suspensión:</strong> {emp.suspension_motivo}
                        </div>
                      )}
                    </div>
                    {susc && (
                      <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-[#F0F5F1] to-[#FAFAF7] border border-c flex items-center gap-3 flex-wrap" data-testid={`cliente-suscripcion-${emp.id}`}>
                        <div className="w-9 h-9 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
                          <Package size={16} />
                        </div>
                        <div className="min-w-[180px]">
                          <div className="text-[10px] uppercase tracking-widest text-secondary-c font-semibold">Suscripción</div>
                          <div className="text-sm font-semibold text-primary-c leading-tight">Plan {susc.plan.nombre}</div>
                          <div className="text-[11px] text-secondary-c">
                            Frecuencia {susc.frecuencia.label}
                            {susc.frecuencia.descuento > 0 ? ` · −${Math.round(susc.frecuencia.descuento * 100)}%` : ""}
                          </div>
                        </div>
                        <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs min-w-[240px]">
                          <div className="p-1.5 rounded bg-white border border-c">
                            <div className="text-[9px] uppercase tracking-wider text-secondary-c">Colaboradores</div>
                            <div className="font-semibold">{susc.nColabs}</div>
                          </div>
                          <div className="p-1.5 rounded bg-white border border-c">
                            <div className="text-[9px] uppercase tracking-wider text-secondary-c">Total del ciclo</div>
                            <div className="font-semibold text-primary-c">{colones(susc.total)}</div>
                          </div>
                          <div className="p-1.5 rounded bg-[#FDF4E1] border border-[#E7D69B] col-span-2 md:col-span-1">
                            <div className="text-[9px] uppercase tracking-wider text-[#8A6D18]">Próximo pago</div>
                            <div className="font-semibold text-[#8A6D18]">{fmtFecha(susc.proximoPagoISO)}</div>
                            <div className="text-[9px] text-[#8A6D18]">
                              {susc.diasRest <= 0 ? "vencido" : `en ${susc.diasRest} d`}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    {c.pagos_pendientes > 0 && (
                      <button
                        onClick={() => setDetalle(emp)}
                        className="np-btn np-btn-primary text-sm !bg-warning !text-primary-c hover:!bg-[#e2b93a]"
                        data-testid={`cliente-aprobar-${emp.id}`}
                        title="Revisar y aprobar los pagos pendientes de este cliente"
                      >
                        <CheckCircle2 size={14} /> Aprobar pago ({c.pagos_pendientes})
                      </button>
                    )}
                    <button onClick={() => setDetalle(emp)} className="np-btn np-btn-outline text-sm" data-testid={`cliente-pagos-${emp.id}`}>
                      <Eye size={14} /> Ver pagos
                    </button>
                    <button
                      onClick={() => descargarZip(emp)}
                      disabled={descargando === emp.id}
                      className="np-btn np-btn-outline text-sm disabled:opacity-50"
                      data-testid={`cliente-download-${emp.id}`}
                      title="Descargar toda la información del cliente en un ZIP"
                    >
                      <Download size={14} /> {descargando === emp.id ? "Generando…" : "Descargar ZIP"}
                    </button>
                    {suspendida ? (
                      <button onClick={() => setConfirmar({ action: "desbloquear", empresa: emp })} className="np-btn np-btn-primary text-sm" data-testid={`cliente-unblock-${emp.id}`}>
                        <Unlock size={14} /> Desbloquear
                      </button>
                    ) : (
                      <button onClick={() => setConfirmar({ action: "bloquear", empresa: emp })} className="np-btn np-btn-outline text-sm border-[#F0C6C6] text-error hover:bg-[#FDEDED]" data-testid={`cliente-block-${emp.id}`}>
                        <Lock size={14} /> Bloquear
                      </button>
                    )}
                    <button
                      onClick={() => setEliminar(emp)}
                      className="np-btn np-btn-outline text-sm border-[#F0C6C6] text-error hover:bg-[#FDEDED]"
                      data-testid={`cliente-delete-${emp.id}`}
                      title="Eliminar cliente permanentemente"
                    >
                      <Trash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detalle && <PagosClienteModal empresa={detalle} onClose={() => { setDetalle(null); load(); }} />}

      {confirmar && (
        <BloqueoModal
          action={confirmar.action}
          empresa={confirmar.empresa}
          onCancel={() => setConfirmar(null)}
          onConfirm={(motivo) => confirmar.action === "bloquear" ? bloquear(confirmar.empresa.id, motivo) : desbloquear(confirmar.empresa.id)}
        />
      )}

      {eliminar && (
        <EliminarClienteModal
          empresa={eliminar}
          onCancel={() => setEliminar(null)}
          onConfirm={() => eliminarCliente(eliminar)}
          onDescargarPrevio={() => descargarZip(eliminar)}
          descargando={descargando === eliminar.id}
        />
      )}
    </>
  );
}

function EliminarClienteModal({ empresa, onCancel, onConfirm, onDescargarPrevio, descargando }) {
  const [confirmText, setConfirmText] = useState("");
  const nombreCanonico = (empresa.razon_social || "").trim();
  const puedeEliminar = confirmText.trim() === nombreCanonico && nombreCanonico.length > 0;
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    setSaving(true);
    try { await onConfirm(); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onCancel}>
      <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="eliminar-cliente-modal">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#FDEDED] text-error flex items-center justify-center">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold">Eliminar cliente</h3>
            <p className="text-xs text-secondary-c">Acción irreversible</p>
          </div>
        </div>
        <p className="text-sm mb-3">
          Se eliminarán <strong>permanentemente</strong> todos los datos de <strong className="text-primary-c">{nombreCanonico || "(sin razón social)"}</strong>:
          empleados, asistencia, incapacidades, rebajos, bonificaciones, contratos, liquidaciones, pagos, comprobantes y usuarios.
        </p>
        <div className="p-3 rounded-xl bg-[#FDF4E1] border border-[#E7D69B] text-xs text-[#8A6D18] mb-4 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>Te recomendamos <strong>descargar un respaldo ZIP antes de eliminar</strong>. No podrás recuperar los datos después.</span>
        </div>
        <button
          type="button"
          onClick={onDescargarPrevio}
          disabled={descargando}
          className="np-btn np-btn-outline w-full mb-3 disabled:opacity-50"
          data-testid="eliminar-descargar-previo"
        >
          <Download size={14} /> {descargando ? "Generando ZIP…" : "Descargar respaldo antes de eliminar"}
        </button>
        <label className="np-label">Para confirmar, escribí la razón social exacta: <strong className="text-primary-c">{nombreCanonico}</strong></label>
        <input
          className="np-input"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder={nombreCanonico}
          autoFocus
          data-testid="eliminar-confirm-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="np-btn np-btn-ghost" data-testid="eliminar-cancel">Cancelar</button>
          <button
            onClick={handle}
            disabled={!puedeEliminar || saving}
            className="np-btn np-btn-primary !bg-error disabled:opacity-40"
            data-testid="eliminar-confirmar"
          >
            <Trash2 size={14} /> {saving ? "Eliminando…" : "Eliminar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BloqueoModal({ action, empresa, onCancel, onConfirm }) {
  const [motivo, setMotivo] = useState("Falta de pago");
  const isBloquear = action === "bloquear";
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onCancel}>
      <div className="np-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="bloqueo-modal">
        <h3 className="font-display text-xl font-semibold mb-2">{isBloquear ? "Bloquear cliente" : "Desbloquear cliente"}</h3>
        <p className="text-sm text-secondary-c mb-4">
          {isBloquear
            ? <>Vas a suspender el acceso de <strong className="text-primary-c">{empresa.razon_social}</strong>. Todos sus usuarios verán "Cuenta suspendida por falta de pago" al intentar iniciar sesión.</>
            : <>Vas a reactivar el acceso de <strong className="text-primary-c">{empresa.razon_social}</strong>. Sus usuarios podrán iniciar sesión inmediatamente.</>}
        </p>
        {isBloquear && (
          <div className="mb-4">
            <label className="np-label">Motivo</label>
            <textarea className="np-input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} data-testid="bloqueo-motivo" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="np-btn np-btn-ghost" data-testid="bloqueo-cancel">Cancelar</button>
          <button onClick={() => onConfirm(motivo)} className={`np-btn np-btn-primary ${isBloquear ? "!bg-error" : ""}`} data-testid="bloqueo-ok">
            {isBloquear ? "Bloquear" : "Desbloquear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PagosClienteModal({ empresa, onClose }) {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verComprobante, setVerComprobante] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/pagos?empresa_id=${empresa.id}`);
      setPagos(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron cargar los pagos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [empresa.id]);

  const aprobar = async (p) => {
    try { await api.put(`/admin/pagos/${p.id}/aprobar`); toast.success("Pago aprobado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
  };
  const rechazar = async (p) => {
    const motivo = window.prompt("Motivo del rechazo (opcional):", "");
    if (motivo === null) return;
    try { await api.put(`/admin/pagos/${p.id}/rechazar`, { motivo }); toast.success("Pago rechazado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="np-card p-6 max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="pagos-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-xl font-semibold">Pagos de {empresa.razon_social}</h3>
            <p className="text-xs text-secondary-c mt-1">Céd. jurídica: {empresa.cedula_juridica || "—"}</p>
          </div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2" data-testid="pagos-modal-close"><XCircle size={18} /></button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-secondary-c">Cargando…</div>
        ) : pagos.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={36} className="mx-auto text-secondary-c opacity-40 mb-2" />
            <div className="font-medium">Aún no hay pagos registrados por este cliente</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="np-table text-sm w-full">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Método</th>
                  <th>Período</th>
                  <th className="text-right">Monto</th>
                  <th>Estado</th>
                  <th>Comprobante</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagos.map(p => (
                  <tr key={p.id} data-testid={`pago-row-${p.id}`}>
                    <td>{fmtFecha(p.fecha_pago)}</td>
                    <td className="capitalize">{p.metodo}</td>
                    <td className="capitalize">{p.periodo}</td>
                    <td className="text-right font-semibold">{colones(p.monto)}</td>
                    <td>
                      {p.estado === "pendiente" && <span className="np-badge np-badge-warning text-[10px]">Pendiente</span>}
                      {p.estado === "aprobado" && <span className="np-badge np-badge-success text-[10px]">Aprobado</span>}
                      {p.estado === "rechazado" && <span className="np-badge np-badge-error text-[10px]">Rechazado</span>}
                    </td>
                    <td>
                      {p.comprobante_base64 && (
                        <button onClick={() => setVerComprobante(p)} className="text-primary-c text-xs underline" data-testid={`pago-view-${p.id}`}>
                          <Eye size={12} className="inline" /> Ver
                        </button>
                      )}
                    </td>
                    <td>
                      {p.estado === "pendiente" ? (
                        <div className="flex gap-1">
                          <button onClick={() => aprobar(p)} className="np-btn np-btn-primary text-xs px-2 py-1" data-testid={`pago-aprobar-${p.id}`}><CheckCircle2 size={11} /> Aprobar</button>
                          <button onClick={() => rechazar(p)} className="np-btn np-btn-outline text-xs px-2 py-1 border-[#F0C6C6] text-error" data-testid={`pago-rechazar-${p.id}`}><XCircle size={11} /> Rechazar</button>
                        </div>
                      ) : (
                        <span className="text-xs text-secondary-c">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {verComprobante && <ComprobanteViewer pago={verComprobante} onClose={() => setVerComprobante(null)} />}
      </div>
    </div>
  );
}

function ComprobanteViewer({ pago, onClose }) {
  const isPDF = (pago.comprobante_nombre || "").toLowerCase().endsWith(".pdf") || (pago.comprobante_base64 || "").startsWith("data:application/pdf");
  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-4 max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium text-sm">Comprobante · {pago.comprobante_nombre || "sin nombre"}</div>
          <button onClick={onClose} className="np-btn np-btn-ghost p-1"><XCircle size={18} /></button>
        </div>
        {isPDF ? (
          <iframe title="comprobante" src={pago.comprobante_base64} className="w-full h-[70vh] rounded-lg border border-c" />
        ) : (
          <img src={pago.comprobante_base64} alt="Comprobante" className="w-full max-h-[70vh] object-contain rounded-lg" />
        )}
      </div>
    </div>
  );
}
